import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaSearch, 
  FaFilter, 
  FaTh, 
  FaList, 
  FaCogs,
  FaEye,
  FaServer,
  FaInfoCircle,
  FaRocket,
  FaBuilding,
  FaCloud
} from 'react-icons/fa';
import useScrollAnimation from '../../../hooks/useScrollAnimation';
// Import du context de langue
import { useLanguage } from '../../../contexts/LanguageContext';
import { Helmet } from 'react-helmet-async';

const ControllerWIFI = () => {
  const { t } = useLanguage(); // Utilisation du hook de traduction
  
  // Mapping des icônes pour les catégories (car les icônes ne sont pas dans le JSON)
  const categoryIcons = {
    'new-generation': FaRocket,
    'campus': FaBuilding,
    'cloud': FaCloud,
    'legacy': FaServer
  };

  // Mapping des couleurs pour les catégories
  const categoryColors = {
    'new-generation': '#fefefeff',
    'campus': '#0ea5e9',
    'cloud': '#10b981',
    'legacy': '#f59e0b'
  };

  const [filteredProducts, setFilteredProducts] = useState(t.wifiController.controllerProducts);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const productsPerPage = 6;

  const [advantagesRef, advantagesVisible] = useScrollAnimation();

  useEffect(() => {
    let filtered = [...t.wifiController.controllerProducts];

    // Filtrage par recherche
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.keyUse.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrage par catégorie
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Tri
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'category':
          return a.category.localeCompare(b.category);
        default:
          return 0;
      }
    });

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, sortBy, t.wifiController.controllerProducts]);

  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const getCategoryInfo = (categoryId) => {
    const cat = t.wifiController.categories.find(c => c.id === categoryId);
    return {
      ...cat,
      icon: categoryIcons[categoryId] || FaServer,
      color: categoryColors[categoryId] || '#64748b'
    };
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.wifiController.seo?.title || "Cisco WiFi Controller Tunisie | S2i"}</title>
        <meta name="description" content={t.wifiController.seo?.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/solutions/wifi-controller" />
        
        {/* Open Graph */}
        <meta property="og:title" content={t.wifiController.seo?.title} />
        <meta property="og:description" content={t.wifiController.seo?.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/CiscoWIFIController/cisco-cw9800m.avif" />
      </Helmet>
      {/* ---------------------------------- */}



      {/* Section Hero */}
      <section style={{
        background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/datacenter.webp')`,
        padding: '6rem 0 4rem 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Éléments décoratifs */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(220, 38, 38, 0.2), rgba(220, 38, 38, 0.1))',
          filter: 'blur(40px)'
        }} />

        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="text-center text-lg-start">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Badge 
                  bg="danger" 
                  className="mb-3"
                  style={{
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}
                >
                  <FaServer className="me-2" />
                  {t.wifiController.hero.badge}
                </Badge>
                
                <h1 style={{
                  fontSize: '3.5rem',
                  fontWeight: 700,
                  color: '#fff',
                  marginBottom: '1.5rem',
                  lineHeight: '1.2'
                }} dangerouslySetInnerHTML={{ __html: t.wifiController.hero.title }} />
                
                <p style={{
                  fontSize: '1.3rem',
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '2rem',
                  lineHeight: '1.6',
                  maxWidth: '600px'
                }}>
                  {t.wifiController.hero.subtitle}
                </p>
                
                <div className="d-flex flex-wrap gap-3">
                  <Button 
                    variant="danger" 
                    size="lg"
                    onClick={() => document.getElementById('solutions-section').scrollIntoView({ behavior: 'smooth' })}
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      border: 'none',
                      boxShadow: '0 4px 16px rgba(220, 38, 38, 0.4)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaEye className="me-2" />
                    {t.wifiController.hero.btnExplore}
                  </Button>
                  <Button 
                    variant="outline-light" 
                    size="lg"
                    onClick={() => window.open('https://www.cisco.com/c/en/us/products/wireless/catalyst-9800-series-wireless-controllers/index.html', '_blank')}
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderWidth: '2px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaInfoCircle className="me-2" />
                    {t.wifiController.hero.btnGuide}
                  </Button>
                </div>
              </motion.div>
            </Col>
            
            <Col lg={4} className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '20px',
                  padding: '2rem',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}
              >
                <img 
                  src="/images/CiscoWIFIController/ControllerImage.png" 
                  alt="WiFi Controller Solutions"
                  style={{
                    width: '300px',
                    height: '180px',
                    objectFit: 'cover',
                    marginBottom: '1rem',
                    borderRadius: '16px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <h4 style={{ color: '#fff', marginBottom: '1rem' }}>
                  {t.wifiController.hero.heroCardTitle}
                </h4>
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                  {t.wifiController.hero.heroCardSub}
                </p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section principale avec filtres et produits */}
      <section id="solutions-section" className="py-5" style={{ backgroundColor: '#f8fafc' }}>
        <Container>
          <Row>
            {/* Sidebar avec filtres */}
            <Col lg={3}>
              <div style={{
                background: '#fff',
                borderRadius: '20px',
                padding: '1.5rem',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                position: 'sticky',
                top: '2rem',
                marginBottom: '2rem'
              }}>
                {/* Recherche */}
                <div className="mb-4">
                  <label style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#4a5568',
                    marginBottom: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block'
                  }}>
                    <FaSearch className="me-2" />
                    {t.wifiController.sidebar.search}
                  </label>
                  <Form.Control
                    type="text"
                    placeholder={t.wifiController.sidebar.placeholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e2e8f0',
                      fontSize: '0.9rem',
                      padding: '0.75rem 1rem'
                    }}
                  />
                </div>

                {/* Filtre par catégorie */}
                <div className="mb-4">
                  <label style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#4a5568',
                    marginBottom: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block'
                  }}>
                    <FaFilter className="me-2" />
                    {t.wifiController.sidebar.category}
                  </label>
                  
                  <div className="d-grid gap-2">
                    <Button
                      variant={selectedCategory === 'all' ? 'danger' : 'outline-secondary'}
                      onClick={() => handleCategoryChange('all')}
                      size="sm"
                      style={{ 
                        borderRadius: '10px', 
                        fontSize: '0.8rem',
                        padding: '0.5rem 0.75rem',
                        textAlign: 'left'
                      }}
                    >
                      {t.wifiController.sidebar.all}
                    </Button>
                    {t.wifiController.categories.map(category => {
                      const Icon = categoryIcons[category.id] || FaServer;
                      return (
                        <Button
                          key={category.id}
                          variant={selectedCategory === category.id ? 'danger' : 'outline-secondary'}
                          onClick={() => handleCategoryChange(category.id)}
                          size="sm"
                          style={{ 
                            borderRadius: '10px', 
                            fontSize: '0.8rem',
                            padding: '0.5rem 0.75rem',
                            textAlign: 'left'
                          }}
                        >
                          <Icon className="me-2" /> {category.name}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Options d'affichage */}
                <div className="mb-4">
                  <label style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#4a5568',
                    marginBottom: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block'
                  }}>
                    {t.wifiController.sidebar.display}
                  </label>
                  <div className="d-flex gap-2">
                    <Button
                      variant={viewMode === 'grid' ? 'danger' : 'outline-secondary'}
                      onClick={() => setViewMode('grid')}
                      size="sm"
                      style={{
                        borderRadius: '10px',
                        flex: 1
                      }}
                    >
                      <FaTh />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'danger' : 'outline-secondary'}
                      onClick={() => setViewMode('list')}
                      size="sm"
                      style={{
                        borderRadius: '10px',
                        flex: 1
                      }}
                    >
                      <FaList />
                    </Button>
                  </div>
                </div>

                {/* Résultats */}
                <div style={{
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                  borderRadius: '12px',
                  border: '1px solid #dee2e6',
                  marginBottom: '1.5rem'
                }}>
                  <p style={{ 
                    color: '#495057', 
                    margin: 0,
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textAlign: 'center'
                  }}>
                    <strong style={{ color: '#dc2626' }}>{filteredProducts.length}</strong> {t.wifiController.sidebar.results}
                  </p>
                  {totalPages > 1 && (
                    <p style={{ 
                      color: '#6c757d', 
                      margin: '0.25rem 0 0 0',
                      fontSize: '0.75rem',
                      textAlign: 'center'
                    }}>
                      {t.wifiController.sidebar.pageOf.split(' ')[0]} {currentPage} {t.wifiController.sidebar.pageOf.split(' ')[1]} {totalPages}
                    </p>
                  )}
                </div>
              </div>
            </Col>

            {/* Zone principale des produits */}
            <Col lg={9}>
              {viewMode === 'grid' ? (
                <Row className="g-4">
                  {paginatedProducts.map((product, index) => {
                    const catInfo = getCategoryInfo(product.category);
                    return (
                      <Col md={6} xl={4} key={product.id}>
                        <motion.div
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                          whileHover={{ y: -5 }}
                          style={{ height: '100%' }}
                        >
                          <Card style={{
                            height: '100%',
                            borderRadius: '20px',
                            border: 'none',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease'
                          }}>
                            <div style={{
                              height: '200px',
                              background: `linear-gradient(135deg, ${catInfo.color}20, ${catInfo.color}40)`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative'
                            }}>
                              <img
                                src={product.image}
                                alt={product.name}
                                style={{
                                  maxWidth: '80%',
                                  maxHeight: '80%',
                                  objectFit: 'contain'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div style={{
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                flexDirection: 'column'
                              }}>
                                <FaServer size={60} color={catInfo.color} />
                                <p style={{ color: '#64748b', marginTop: '1rem', textAlign: 'center' }}>
                                  {t.wifiController.specs.noImage}
                                </p>
                              </div>
                              <Badge
                                style={{
                                  position: 'absolute',
                                  top: '1rem',
                                  right: '1rem',
                                  backgroundColor: catInfo.color,
                                  borderRadius: '25px',
                                  padding: '0.5rem 1rem',
                                  fontSize: '0.75rem'
                                }}
                              >
                                {catInfo.name}
                              </Badge>
                            </div>

                            <Card.Body style={{ padding: '1.5rem' }}>
                              <h5 style={{
                                fontWeight: 700,
                                color: '#1e293b',
                                marginBottom: '0.75rem',
                                fontSize: '1.1rem',
                                lineHeight: 1.3
                              }}>
                                {product.name}
                              </h5>

                              <p style={{
                                color: '#64748b',
                                fontSize: '0.85rem',
                                lineHeight: 1.5,
                                marginBottom: '1rem'
                              }}>
                                {product.description.length > 120 
                                  ? `${product.description.substring(0, 120)}...`
                                  : product.description
                                }
                              </p>

                              {/* Capacités clés */}
                              <div className="mb-3">
                                <div style={{
                                  background: '#f8fafc',
                                  borderRadius: '12px',
                                  padding: '1rem',
                                  border: '1px solid #e2e8f0'
                                }}>
                                  <div className="text-center">
                                    <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                                      {t.wifiController.specs.capabilities}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b', lineHeight: '1.3' }}>
                                      {product.specifications.accessPoints} {t.wifiController.specs.ap} • {product.specifications.clients} {t.wifiController.specs.clients}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Options disponibles */}
                              <div className="mb-3">
                                <h6 style={{
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  color: '#4a5568',
                                  marginBottom: '0.75rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  <FaCogs size={12} color="#dc2626" className="me-2" />
                                  {t.wifiController.specs.features}
                                </h6>
                                <div style={{
                                  background: '#f1f5f9',
                                  borderRadius: '8px',
                                  padding: '0.75rem',
                                  border: '1px solid #e2e8f0'
                                }}>
                                  {product.options.split(',').map((option, idx) => (
                                    <div key={idx} style={{
                                      fontSize: '0.8rem',
                                      color: '#475569',
                                      marginBottom: idx < product.options.split(',').length - 1 ? '0.3rem' : '0',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      <span style={{
                                        width: '4px',
                                        height: '4px',
                                        backgroundColor: '#dc2626',
                                        borderRadius: '50%',
                                        marginRight: '0.5rem',
                                        flexShrink: 0
                                      }}></span>
                                      <span style={{ fontWeight: 500 }}>{option.trim()}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                            </Card.Body>
                          </Card>
                        </motion.div>
                      </Col>
                    );
                  })}
                </Row>
              ) : (
                <div className="d-grid gap-4">
                  {paginatedProducts.map((product, index) => {
                    const catInfo = getCategoryInfo(product.category);
                    return (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <Card style={{
                          borderRadius: '20px',
                          border: 'none',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                          overflow: 'hidden'
                        }}>
                          <Row className="g-0">
                            <Col md={4}>
                              <div style={{
                                height: '250px',
                                background: `linear-gradient(135deg, ${catInfo.color}20, ${catInfo.color}40)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                              }}>
                                <img
                                  src={product.image}
                                  alt={product.name}
                                  style={{
                                    maxWidth: '90%',
                                    maxHeight: '90%',
                                    objectFit: 'contain'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <div style={{
                                  display: 'none',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%',
                                  height: '100%',
                                  flexDirection: 'column'
                                }}>
                                  <FaServer size={80} color={catInfo.color} />
                                  <p style={{ color: '#64748b', marginTop: '1rem', textAlign: 'center' }}>
                                    {t.wifiController.specs.noImage}
                                  </p>
                                </div>
                                <Badge
                                  style={{
                                    position: 'absolute',
                                    top: '1rem',
                                    left: '1rem',
                                    backgroundColor: catInfo.color,
                                    borderRadius: '25px',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  {catInfo.name}
                                </Badge>
                              </div>
                            </Col>
                            <Col md={8}>
                              <Card.Body style={{ padding: '2rem' }}>
                                <h4 style={{
                                  fontWeight: 700,
                                  color: '#1e293b',
                                  marginBottom: '1rem'
                                }}>
                                  {product.name}
                                </h4>

                                <p style={{
                                  color: '#64748b',
                                  fontSize: '1rem',
                                  lineHeight: 1.6,
                                  marginBottom: '1.5rem'
                                }}>
                                  {product.description}
                                </p>

                                <Row>
                                  <Col sm={6}>
                                    <div className="mb-3">
                                      <small style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {t.wifiController.specs.capabilities}
                                      </small>
                                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                        {product.specifications.accessPoints} {t.wifiController.specs.ap} • {product.specifications.clients} {t.wifiController.specs.clients}<br/>
                                        {product.specifications.throughput} {t.wifiController.specs.debit}
                                      </div>
                                    </div>
                                  </Col>
                                  <Col sm={6}>
                                    <div className="mb-3">
                                      <small style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {t.wifiController.specs.features}
                                      </small>
                                      <div style={{
                                        background: '#f8fafc',
                                        borderRadius: '6px',
                                        padding: '0.5rem',
                                        marginTop: '0.25rem'
                                      }}>
                                        {product.options.split(',').slice(0, 3).map((option, idx) => (
                                          <div key={idx} style={{
                                            fontSize: '0.8rem',
                                            color: '#475569',
                                            marginBottom: idx < 2 ? '0.2rem' : '0',
                                            display: 'flex',
                                            alignItems: 'center'
                                          }}>
                                            <span style={{
                                              width: '3px',
                                              height: '3px',
                                              backgroundColor: '#dc2626',
                                              borderRadius: '50%',
                                              marginRight: '0.4rem',
                                              flexShrink: 0
                                            }}></span>
                                            <span style={{ fontWeight: 500 }}>{option.trim()}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Col>
                          </Row>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-5">
                  <div className="d-flex gap-2">
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Button
                        key={i + 1}
                        variant={currentPage === i + 1 ? "danger" : "outline-danger"}
                        onClick={() => setCurrentPage(i + 1)}
                        style={{
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          fontWeight: 600
                        }}
                      >
                        {i + 1}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Message si aucun résultat */}
              {filteredProducts.length === 0 && (
                <div className="text-center py-5">
                  <FaServer size={64} color="#64748b" />
                  <h4 style={{ color: '#64748b', marginTop: '1rem' }}>
                    {t.wifiController.sidebar.noResults}
                  </h4>
                  <p style={{ color: '#64748b' }}>
                    {t.wifiController.sidebar.noResultsSub}
                  </p>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section des avantages clés */}
      <section 
        ref={advantagesRef} 
        className="advantages-section-controller" 
        style={{ 
          padding: '8rem 0', 
          background: `linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(71, 85, 105, 0.95) 100%), url('/images/datacenter2.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />

        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row>
            <Col lg={12} className="text-center mb-5">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="section-title-controller" style={{ 
                  fontSize: '2.8rem', 
                  fontWeight: 700, 
                  color: '#ffffff',
                  marginBottom: '1.5rem',
                  textShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  {t.wifiController.advantagesSection.title} <span style={{ color: '#dc2626' }}>{t.wifiController.advantagesSection.titleRed}</span>
                </h2>
                <p className="section-subtitle-controller" style={{
                  fontSize: '1.3rem',
                  color: 'rgba(255,255,255,0.9)',
                  maxWidth: '700px',
                  margin: '0 auto',
                  lineHeight: '1.6',
                  textShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  {t.wifiController.advantagesSection.subtitle}
                </p>
              </motion.div>
            </Col>
          </Row>

          <Row className="g-4">
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                viewport={{ once: true }}
                whileHover={{ 
                  y: -12, 
                  scale: 1.03,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '20px',
                  padding: '2.5rem 2rem',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  backdropFilter: 'blur(10px)',
                  textAlign: 'left'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #dc2626, rgba(220,38,38,0.6))',
                  borderRadius: '20px 20px 0 0'
                }} />

                <div style={{
                  background: 'linear-gradient(135deg, rgba(220,38,38,0.1), rgba(220,38,38,0.05))',
                  borderRadius: '16px',
                  width: '70px',
                  height: '70px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '2rem',
                  position: 'relative',
                  zIndex: 1,
                  border: '1px solid rgba(220,38,38,0.15)'
                }}>
                  <FaServer style={{
                    fontSize: '2rem',
                    color: '#dc2626',
                    filter: 'drop-shadow(0 2px 4px rgba(220,38,38,0.2))'
                  }} />
                </div>

                <h3 style={{
                  color: '#1a202c',
                  fontWeight: 700,
                  marginBottom: '1.25rem',
                  fontSize: '1.3rem',
                  lineHeight: '1.3',
                  position: 'relative',
                  zIndex: 1
                }}>
                  {t.wifiController.advantagesSection.card1.title}
                </h3>

                <p style={{
                  color: '#4a5568',
                  margin: 0,
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  position: 'relative',
                  zIndex: 1
                }}>
                  {t.wifiController.advantagesSection.card1.desc}
                </p>
              </motion.div>
            </Col>

            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                whileHover={{ 
                  y: -12, 
                  scale: 1.03,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '20px',
                  padding: '2.5rem 2rem',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  backdropFilter: 'blur(10px)',
                  textAlign: 'left'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #dc2626, rgba(220,38,38,0.6))',
                  borderRadius: '20px 20px 0 0'
                }} />

                <div style={{
                  background: 'linear-gradient(135deg, rgba(220,38,38,0.1), rgba(220,38,38,0.05))',
                  borderRadius: '16px',
                  width: '70px',
                  height: '70px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '2rem',
                  position: 'relative',
                  zIndex: 1,
                  border: '1px solid rgba(220,38,38,0.15)'
                }}>
                  <FaRocket style={{
                    fontSize: '2rem',
                    color: '#dc2626',
                    filter: 'drop-shadow(0 2px 4px rgba(220,38,38,0.2))'
                  }} />
                </div>

                <h3 style={{
                  color: '#1a202c',
                  fontWeight: 700,
                  marginBottom: '1.25rem',
                  fontSize: '1.3rem',
                  lineHeight: '1.3',
                  position: 'relative',
                  zIndex: 1
                }}>
                  {t.wifiController.advantagesSection.card2.title}
                </h3>

                <p style={{
                  color: '#4a5568',
                  marginBottom: '2rem',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  position: 'relative',
                  zIndex: 1
                }}>
                  {t.wifiController.advantagesSection.card2.desc}
                </p>

                <div style={{ fontSize: '0.95rem', marginBottom: '0', position: 'relative', zIndex: 1 }}>
                  {t.wifiController.advantagesSection.card2.list.map((feature, idx) => (
                    <motion.div 
                      key={idx} 
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 + (idx * 0.1) }}
                      viewport={{ once: true }}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: idx < 2 ? '0.75rem' : '0',
                        padding: '0.25rem 0'
                      }}
                    >
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#dc2626',
                        marginRight: '1rem',
                        marginTop: '0.65rem',
                        flexShrink: 0,
                        opacity: 0.8
                      }} />
                      <span style={{
                        fontSize: '0.90rem',
                        color: '#475569',
                        fontWeight: 500,
                        lineHeight: '1.5'
                      }}>
                        {feature}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default ControllerWIFI;